import * as THREE from "three";

/**
 * Equirectangular 360 panorama, presented as a sphere the Scale Continuum can
 * act on like anything else.
 *
 * The sphere is rendered back-face only, which gives the interaction its whole
 * character: shrink it and you are looking *into* a small hollow globe with the
 * far interior curving away from you — a snow globe of the place. Grow it and
 * the same surface closes around you and you are standing in it. One object,
 * one gesture, both readings physically coherent. A double-sided sphere would
 * show a mirrored exterior when small, which breaks the illusion immediately.
 *
 * Honest labelling matters here more than anywhere else in the product. This is
 * rotational immersion only — there is no parallax and leaning does nothing,
 * because a panorama has no depth. The brief's language for this is
 * "360 Preview", and it must never be presented as a walkable world.
 */

export interface PanoramaManifest {
  readonly schemaVersion: 1;
  readonly kind: "equirect-360";
  readonly prompt: string;
  readonly model: string;
  readonly size: { readonly width: number; readonly height: number };
  readonly seam: {
    readonly repaired: boolean;
    readonly errorBefore: number;
    readonly errorAfter: number;
  };
  readonly poles: {
    readonly topStdDev: number;
    readonly bottomStdDev: number;
  };
  readonly files: Readonly<Record<string, string>>;
}

export interface LoadedPanorama {
  readonly mesh: THREE.Mesh;
  readonly loadMs: number;
  readonly name: string;
  readonly manifest: PanoramaManifest | null;
  /** Truthful presentation label, per the brief's representation ladder. */
  readonly truthLabel: "360 Preview";
  /** Mean edge difference, 0-255. Under ~12 reads as continuous. */
  readonly seamError: number | null;
}

const SPHERE_RADIUS = 1;

function loadTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        // The horizontal wrap is what lets the repaired seam actually close.
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.anisotropy = 8;
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        resolve(texture);
      },
      undefined,
      () => reject(new Error(`panorama texture failed: ${url}`))
    );
  });
}

export async function loadPanorama(options: {
  readonly folder: string | null;
  readonly url: string | null;
  readonly onProgress: (message: string) => void;
}): Promise<LoadedPanorama> {
  const started = performance.now();

  let manifest: PanoramaManifest | null = null;
  let textureUrl: string;
  let name: string;

  if (options.folder !== null) {
    const response = await fetch(`/panoramas/${options.folder}/panorama.json`, {
      cache: "no-cache"
    });
    if (!response.ok) {
      throw new Error(`panorama manifest -> ${response.status}`);
    }
    manifest = (await response.json()) as PanoramaManifest;
    textureUrl = `/panoramas/${options.folder}/${manifest.files.panorama ?? "panorama.png"}`;
    name = manifest.prompt.slice(0, 48);
  } else {
    textureUrl = options.url ?? "";
    name = "Panorama";
  }

  options.onProgress(`loading ${name}`);
  const texture = await loadTexture(textureUrl);

  const geometry = new THREE.SphereGeometry(SPHERE_RADIUS, 96, 64);
  // Flipping X keeps the panorama reading the right way round from inside;
  // without it the scene is mirrored and text/architecture look subtly wrong.
  geometry.scale(-1, 1, 1);

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.FrontSide, // front of the inverted sphere == its interior
      toneMapped: false
    })
  );
  mesh.name = "panorama";

  return {
    mesh,
    loadMs: Math.round(performance.now() - started),
    name,
    manifest,
    truthLabel: "360 Preview",
    seamError: manifest?.seam.errorAfter ?? null
  };
}
